import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Clock, Layers, TrendingUp, Landmark, Zap, Shield, Star } from 'lucide-react';
import { SelectedTransactions } from './SelectedTransactions';
import { CapitalSolutionsJsonLd } from './CapitalSolutionsJsonLd';

/* Ticket-size bands are indicative and drawn from the GP Capital
 * mandate envelope. Real transaction fit is subject to underwriting,
 * counterparty availability and definitive documentation. Kept
 * commercially credible — not overstated.
 */
export const solutions = [
  { icon: Building2, name: 'Senior Debt',          ticket: 'A$10m – A$100m+',  desc: 'First-lien secured facilities for stabilised and transitional assets. Term loans, revolving lines and amortising structures from institutional lenders.' },
  { icon: Clock,     name: 'Bridge Finance',       ticket: 'A$10m – A$60m',    desc: 'Short-term capital for acquisitions, repositioning and time-sensitive transactions. Fast execution from private credit providers with flexible structures.' },
  { icon: Layers,    name: 'Construction Finance', ticket: 'A$20m – A$150m+',  desc: 'Development and construction funding with staged drawdowns, cost-to-complete monitoring and milestone-based disbursement.' },
  { icon: TrendingUp,name: 'Mezzanine Capital',    ticket: 'A$5m – A$50m',     desc: 'Subordinated debt and second-lien facilities that fill the gap between senior lending and equity. Flexible structures for complex transactions.' },
  { icon: Star,      name: 'Preferred Equity',     ticket: 'A$10m – A$75m',    desc: 'Structured preferred equity providing downside protection with upside participation. Suitable for recapitalisations, joint ventures and development.' },
  { icon: Landmark,  name: 'Equity Placements',    ticket: 'A$5m – A$50m',     desc: 'Co-investment and direct equity placements with institutional investors and family offices seeking aligned opportunities.' },
  { icon: Shield,    name: 'Structured Finance',   ticket: 'A$20m – A$150m+',  desc: 'Bespoke structures including unitranche, split-note, A/B note and other customised capital solutions for complex situations.' },
  { icon: Zap,       name: 'Special Situations',   ticket: 'A$10m – A$100m+',  desc: 'Distressed debt, opportunistic lending, restructuring capital and non-standard transactions requiring flexible, relationship-driven execution.' },
];

export const CapitalSolutionsPage = () => (
  <div data-testid="capital-solutions-page-content">
    <CapitalSolutionsJsonLd />
    {/* Solutions Grid */}
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="font-inter text-sm text-[var(--gpc-ink-soft)] uppercase tracking-wider mb-3">Full Capital Stack</p>
          <h2 className="font-grotesk text-3xl font-semibold text-foreground mb-4">
            Capital solutions across the structure
          </h2>
          <p className="font-inter text-muted-foreground leading-relaxed">
            GP Capital facilitates access to private capital across the full spectrum of debt and equity. Each solution is sourced through our curated lender and investor network and executed via a controlled, competitive process.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((sol) => (
            <div
              key={sol.name}
              className="group bg-background border rounded-xl p-6 hover:shadow-card hover:border-teal/40 transition-[border-color,box-shadow] flex flex-col"
              data-testid={`capital-solution-${sol.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center mb-4">
                <sol.icon className="w-5 h-5 text-teal" />
              </div>
              <h3 className="font-grotesk text-base font-semibold text-foreground mb-2">{sol.name}</h3>
              <p className="font-inter text-sm text-muted-foreground leading-relaxed flex-1">{sol.desc}</p>
              <div className="mt-4 pt-3 border-t border-dashed border-border/60">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                  Indicative ticket
                </p>
                <p
                  className="font-mono text-xs text-foreground tabular-nums"
                  data-testid={`ticket-band-${sol.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {sol.ticket}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[11px] text-[var(--gpc-ink-soft)] max-w-3xl">
          Ticket ranges are indicative only. Actual transaction fit depends on asset class, structure, jurisdiction, counterparty availability and definitive documentation.
        </p>
      </div>
    </section>

    {/* Selected Transactions — representative, anonymised */}
    <SelectedTransactions />

    {/* Process */}
    <section className="py-16 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <h3 className="font-grotesk text-2xl font-semibold text-foreground mb-3">Execution Process</h3>
          <p className="font-inter text-muted-foreground leading-relaxed">
            Every capital solution follows our controlled platform workflow — from deal preparation through to counterparty engagement, bid collection and close.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          {[
            { step: '01', title: 'Mandate & Preparation', text: 'Understand the capital requirement, prepare an institutional-quality information package and define the target counterparty profile.' },
            { step: '02', title: 'Selective Distribution', text: 'Circulate to a curated set of pre-qualified lenders and investors matched by mandate, geography and product type.' },
            { step: '03', title: 'Bid Collection', text: 'Manage the NDA-gated deal room, Q&A process and structured bid submission through the Capital Access Platform.' },
            { step: '04', title: 'Execution & Close', text: 'Compare indicative terms, shortlist counterparties, manage best-and-final rounds and coordinate through to documentation and close.' },
          ].map((s) => (
            <div key={s.step}>
              <span className="font-grotesk text-xs text-muted-foreground font-semibold tracking-widest">{s.step}</span>
              <h4 className="font-grotesk font-semibold text-foreground mt-2 mb-2">{s.title}</h4>
              <p className="font-inter text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Disclaimer + CTA */}
    <section className="py-16" style={{ background: 'var(--gpc-panel)', borderTop: '1px solid var(--gpc-line-soft)' }}>
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p className="gpc-eyebrow" style={{ marginBottom: '12px' }}>Next step</p>
        <h3
          style={{
            fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
            color: 'var(--gpc-ink)',
            marginBottom: '14px',
          }}
        >
          Discuss a capital requirement.
        </h3>
        <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.95rem', color: 'var(--gpc-ink-soft)', marginBottom: '28px' }}>
          All opportunities are indicative, subject to underwriting, diligence, approvals and definitive documentation.
        </p>
        <Link to="/contact" className="gpc-cta-primary" data-testid="capsol-cta-submit">
          Submit a Deal <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  </div>
);
